import { useCallback, useEffect, useRef, useState } from 'react';
// Route imports
import { useLocation } from 'react-router-dom';
import { searchFuncApi } from '../../../utilities/api/searchFuncApi';
import { chapterAPI } from '../../../utilities/api';
import { PDFExport } from '@progress/kendo-react-pdf';
import _ from 'lodash';

export const useRuleSearchController = (props?: any) => {
  const EMPTY_VALUE = '';
  let location = useLocation();
  const [query, setQuery] = useState<string>(
    new URLSearchParams(location.search).get('q') ?? EMPTY_VALUE
  );
  const [results, setResults] = useState<any>();
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [pageCount, setPageCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [titleDialogOpen, setTitleDialogOpen] = useState<boolean>(false);
  const [selectedChapterHtml, setSelectedChapterHtml] = useState<any>('');
  const [selectedChapter, setSelectedChapter] = useState<any>(null);
  const [chapterDialogOpen, setChapterDialogOpen] = useState<boolean>(false);
  const [titleData, setTitleData] = useState<any>([]);
  const pdfExport = useRef<PDFExport>(null);
  // var queryData: string = '';

  // #region input handlers
  const handleChange = useCallback((event: any) => {
    setQuery(event.target.value);
    // the eslint warning here is fine to ignore this is used only at runtime
    // if the user selects an item from the suggestions dropdown
    // queryData = event.target.value;
    setResults([]);
    setPageCount(0);
  }, []);

  // # this is for the delay in triggering search suggestions after the user stops typing
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      showSuggestions(query);
    }, 150);
    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  const handleSuggestClose = (event: any) => {
    setQuery(event.value);
    setSuggestions([]);
  };
  // #endregion input handlers

  // #region search suggestions
  /**
   * @description queries azure function to return list suggestions
   * @returns suggestions [{text: string, queryPlusText: string}]
   */
  const showSuggestions = async (suggestionText: any) => {
    let suggestionResults: any = await searchFuncApi.httpSearchFunction('Suggest', {
      q: suggestionText,
      top: 5,
      suggester: 'suggester',
    });
    let suggestList = [];
    if (suggestionResults.data.suggestions) {
      for (var i = 0; i < suggestionResults.data.suggestions.length; i++) {
        suggestList.push(suggestionResults.data.suggestions[i].queryPlusText);
      }
    }
    setSuggestions(suggestList);
  };

  const handleSuggestionClick = useCallback((suggestion: any) => {
    // the line below is if we used the suggestion text not queryPlusText
    // let newQuery = queryData.replace(/[\S]+$/, suggestion);
    setQuery(suggestion);
    setSuggestions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // #endregion

  // #region search handlers
  /**
   * @description triggers azure function for search on page change
   */
  const changePageAndSearch = async (pageState?: any) => {
    setSuggestions([]);
    setLoading(true);
    let searchResults: any = await searchFuncApi.httpSearchFunction('Search', {
      q: query,
      top: pageState ? pageState.take : 5,
      skip: pageState ? pageState.skip : 0,
      filters: [],
    });
    setResults(searchResults.data.results);
    if (pageCount !== searchResults.data.count) {
      setPageCount(searchResults.data.count);
    }
    setLoading(false);
  };

  /**
   * @description Gets the page state from the pager component to pass to handle view of search results
   * @param pageState { take: number, skip: number }
   */
  const handlePageChange = (pageState: any) => {
    changePageAndSearch(pageState);
  };

  /**
   * @description triggers azure function for search
   */
  const search = async (event?: any) => {
    if (event) event.preventDefault();
    setSuggestions([]);
    setLoading(true);
    let searchResults: any = await searchFuncApi.httpSearchFunction('Search', {
      q: query,
      top: 5,
      skip: 0,
      filters: [],
    });
    setResults(searchResults.data.results);
    if (pageCount !== searchResults.data.count) {
      setPageCount(searchResults.data.count);
    }
    setLoading(false);
  };
  // #endregion search handlers

  // #region title dialog
  const toggleTitleDialog = (event?: any, titleId?: number) => {
    setTitleData([]);
    titleDialogOpen ? setTitleDialogOpen(false) : setTitleDialogOpen(true);
    if (titleId) {
      getTitleChapters(titleId);
    }
  };

  const getTitleChapters = async (titleId?: number) => {
    if (titleId) {
      let chapters = await chapterAPI.getChaptersByTitleId(titleId);
      setTitleData(chapters);
    }
  };
  // #endregion toggle dialog

  // #region PDF export
  const exportPDF = () => {
    if (pdfExport.current) {
      pdfExport.current.save();
    }
  };
  // #endregion

  // #region chapter dialog
  const toggleChapterDialog = (event?: any, chapter?: any) => {
    if (chapter) {
      setSelectedChapterHtml(chapter.ChapterHtml);
      setSelectedChapter(chapter);
    } else {
      setSelectedChapterHtml('');
      setSelectedChapter(null);
    }
    chapterDialogOpen ? setChapterDialogOpen(false) : setChapterDialogOpen(true);
  };
  // #endregion

  // #region search keyword highlights
  const getHighlights = (searchData: any) => {
    let highLightResult = '';
    const replaceText = 'No Text Found';

    let htmlResult: string;

    let escapedSearch = _.escapeRegExp(query);
    if (escapedSearch === '') {
      if (searchData.document.ChapterHtml) {
        htmlResult = searchData.document.ChapterHtml;
      } else {
        htmlResult = '';
      }
    } else {
      highLightResult = `<span>${searchData.document.ChapterHtml}</span>`;
      const regex = new RegExp(`(${escapedSearch})`, 'gi');
      let parts =
        searchData.document.ChapterHtml !== null
          ? searchData.document.ChapterHtml.split(regex)
          : replaceText.split(regex);
      parts.forEach((part: any, index: number) => {
        parts[index] = regex.test(part) ? `<mark key=${index}>${part}</mark>` : `${part}`;
      });

      htmlResult = parts.join('');
    }
    let newSearchData = { ...searchData.document, UpdatedChapterHtml: htmlResult };
    searchData.document = newSearchData;
    return searchData;
  };
  // #endregion

  return {
    // variables and state
    query,
    location,
    results,
    suggestions,
    pageCount,
    loading,
    titleDialogOpen,
    titleData,
    pdfExport,
    chapterDialogOpen,
    selectedChapterHtml,
    selectedChapter,
    // functions
    handleChange,
    handleSuggestClose,
    handleSuggestionClick,
    search,
    toggleTitleDialog,
    toggleChapterDialog,
    getTitleChapters,
    handlePageChange,
    exportPDF,
    getHighlights,
  };
};
